import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { EditNote as EditNoteIcon } from '@mui/icons-material';

/**
 * Modal para edición masiva de templates de usuarios de dominio
 */
const MassEditTemplateModal = ({ 
  open, 
  onClose, 
  selectedUsers = [], 
  templates = [],
  onSave 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resetear el formulario cuando se abre el modal
  useEffect(() => {
    if (open) {
      setSelectedTemplate('');
      setError(null);
    }
  }, [open]);

  const handleTemplateChange = (event) => {
    setSelectedTemplate(event.target.value);
    setError(null);
  };

  const handleSave = async () => {
    // Validar que se haya seleccionado un template
    if (!selectedTemplate) {
      setError('Por favor selecciona un template');
      return;
    }

    // Validar que haya usuarios seleccionados
    if (selectedUsers.length === 0) {
      setError('No hay usuarios seleccionados');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Llamar a la función de guardado pasada como prop
      await onSave(selectedUsers, selectedTemplate);
      
      // Cerrar el modal después de guardar exitosamente
      onClose();
    } catch (err) {
      setError(err.message || 'Error al actualizar los templates');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditNoteIcon color="primary" />
          <Typography variant="h6">
            Edición Masiva de Templates
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Se actualizará el template para <strong>{selectedUsers.length}</strong> usuario(s) seleccionado(s).
        </Typography>

        <FormControl fullWidth required>
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            id="template-select"
            value={selectedTemplate}
            label="Template"
            onChange={handleTemplateChange}
            disabled={loading}
          >
            <MenuItem value="">
              <em>Selecciona un template</em>
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.template_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedUsers.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Usuarios seleccionados:
            </Typography>
            <Box sx={{ maxHeight: 150, overflowY: 'auto', mt: 1 }}>
              {selectedUsers.map((user, index) => (
                <Typography key={user.id || index} variant="body2" sx={{ py: 0.5 }}>
                  • {user.email}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !selectedTemplate}
          startIcon={loading ? <CircularProgress size={20} /> : <EditNoteIcon />}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MassEditTemplateModal;
